import { jsx as _jsx } from "react/jsx-runtime";
import { useMeasureableHandle } from '#pplx/frontend/aether/internal/useMeasureableHandle.js';
import { useRadixProps } from '#pplx/frontend/aether/internal/useRadixProps.js';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
const getSwitchClasses = cva(classNames('reset interactable relative inline-block shrink-0 rounded-full shadow-inset-1', 
// animations
'transition-colors duration-150'), {
    variants: {
        size: {
            large: 'h-[26px] w-11',
            default: 'h-[22px] w-9',
            small: 'h-[14px] w-6',
        },
        disabled: {
            false: 'cursor-pointer',
            true: 'cursor-default opacity-50',
        },
        checked: {
            false: 'bg-action-off',
            true: 'bg-action-on',
        },
    },
    compoundVariants: [],
    defaultVariants: {
        size: 'default',
        disabled: false,
        checked: false,
    },
});
const getSwitchThumbClasses = cva(classNames('pointer-events-none absolute block rounded-full', 
// colors
'bg-base dark:bg-inverse', 
// shadow - using before pseudo-element for layered shadow effect
"before:content-[''] before:absolute before:inset-0 before:rounded-full before:bg-base dark:before:bg-white dark:before:deprecated-shadow-md", 
// animations
'transition-transform before:transition-opacity duration-150 before:duration-150'), {
    variants: {
        size: {
            large: 'size-5 data-[state=checked]:translate-x-[18px] left-three top-three',
            default: 'size-4 data-[state=checked]:translate-x-[14px] left-three top-three',
            small: 'size-2.5 data-[state=checked]:translate-x-[10px] left-two top-two',
        },
        disabled: {
            false: '',
            true: 'before:opacity-20',
        },
        checked: {
            false: '',
            true: '',
        },
    },
    compoundVariants: [],
    defaultVariants: {
        size: 'default',
        disabled: false,
        checked: false,
    },
});
/**
 * Toggle switch component supporting controlled and uncontrolled usage with
 * multiple sizes.
 *
 * @category components
 * @keywords switch, components, toggle, component, supporting, controlled,
 *   uncontrolled, usage, with, multiple, sizes, disabled, radio-group, chip
 * @whenToUse Use Switch for binary on/off settings that take effect
 *   immediately, such as enabling notifications, toggling dark mode, or
 *   activating a feature flag. Switches are ideal for settings panels,
 *   preference forms, and anywhere a user needs to toggle between two states
 *   with instant feedback. For choices that require form submission or have
 *   more than two options, use other form controls like Button or RadioGroup.
 * @guidelineDo
 *   - Always give the switch an accessible name that clearly describes what it controls — the
 *     type system requires either aria-label or aria-labelledby
 *   - Use onCheckedChange to respond to toggle events — the callback receives the new boolean
 *     checked state directly
 *   - Use the default size for standard forms and settings, and the small size for compact UI
 *     areas like table rows or menu items
 *   - Pair the switch with a visible label in the UI layout so sighted users understand what it
 *     controls
 * @guidelineDont
 *   - Don't use Switch for actions that require a submission step — switches should take effect
 *     immediately upon toggling
 *   - Don't use Switch when there are more than two mutually exclusive options — use RadioGroup
 *     instead
 *   - Don't rely solely on color to indicate the checked state — the switch uses both color and
 *     thumb position to convey state
 *   - Don't override the tabIndex prop unless you are building a DropdownMenu integration — it is
 *     marked as internal and defaults to 0
 * @accessibility
 *   - Provides the switch ARIA role, aria-checked state management, and keyboard toggling via
 *     Space key
 *   - The accessible name comes from aria-label when no visible label is rendered, or from
 *     aria-labelledby referencing a visible label element — there is no built-in visible label,
 *     so pair with a visible label in the surrounding layout where possible
 *   - The component supports the interactable focus ring pattern for keyboard focus indication via
 *     the 'reset interactable' class on the root element
 *   - Disabled switches set opacity to 50% and change to cursor-default, with the disabled
 *     attribute preventing interaction
 *   - The thumb uses data-[state=checked] for its translate position, ensuring the visual position
 *     always matches the ARIA checked state
 * @relatedComponents
 *   - radio-group
 *   - chip
 */
export function Switch({ ref, checked = false, onCheckedChange, 'aria-labelledby': ariaLabelledBy, 'aria-label': ariaLabel, size = 'default', disabled = false, tabIndex = 0, 
// We intentionally capture the rest of props that could be cloned/passed in by 3rd-party libraries
// such as Radix UI for their own internal usage such as data-state, onFocus, onBlur, etc.
...props }) {
    const targetRef = useMeasureableHandle(ref);
    // Manually pick the props that Radix UI passes in for their own internal usage
    // and pass them down to Radix's SwitchPrimitive.
    const __radixProps = useRadixProps(props);
    return (_jsx(SwitchPrimitive.Root, { ref: targetRef, ...__radixProps, checked: checked, onCheckedChange: onCheckedChange, disabled: disabled, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, className: getSwitchClasses({ size, disabled, checked }), tabIndex: tabIndex, children: _jsx(SwitchPrimitive.Thumb, { className: getSwitchThumbClasses({ size, disabled, checked }) }) }));
}
//# sourceMappingURL=index.js.map